import { authApi } from '@milliswift/api-client'
import { Button, Field, Input } from '@milliswift/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle2, Mail } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { AuthCard, AuthPage } from '../components/AuthCard'
import { forgotPasswordSchema } from '../lib/schemas'

/**
 * Password reset request.
 *
 * Always shows the same confirmation regardless of whether the email exists —
 * the one place where NOT reporting the truth is the security requirement,
 * since an account-existence oracle is exactly what an attacker wants.
 */
export default function ForgotPassword() {
  const [requested, setRequested] = useState(false)

  const form = useForm<{ email: string }>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })

  const submit = form.handleSubmit(async (values) => {
    // Errors are swallowed deliberately: the response must be identical
    // whether or not the address is registered.
    await authApi.forgotPassword(values.email).catch(() => undefined)
    setRequested(true)
  })

  if (requested) {
    return (
      <AuthPage>
        <AuthCard
          title="Check your email"
          footer={
            <Link to="/login" className="font-medium text-accent underline-offset-4 hover:underline">
              Back to sign in
            </Link>
          }
        >
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-soft">
              <CheckCircle2 className="h-6 w-6 text-success-text" aria-hidden />
            </span>
            <p className="text-sm text-muted">
              If that address belongs to a milliswift account, a reset link is on its way.
              The link expires in an hour.
            </p>
          </div>
        </AuthCard>
      </AuthPage>
    )
  }

  return (
    <AuthPage>
      <AuthCard
        title="Reset your password"
        subtitle="We'll email you a single-use reset link."
        footer={
          <>
            Remembered it?{' '}
            <Link to="/login" className="font-medium text-accent underline-offset-4 hover:underline">
              Sign in
            </Link>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-4" noValidate>
          <Field label="Email" required error={form.formState.errors.email?.message}>
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              leading={<Mail className="h-4 w-4" />}
              {...form.register('email')}
            />
          </Field>

          <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
            Send reset link
          </Button>
        </form>
      </AuthCard>
    </AuthPage>
  )
}
